import React, { useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import TableLightBackground from '../../../Components/TableLightBackground/TableLightBackground';
import { RootState } from '../../../redux/root';
import useFilterActions from '../../../pages/Responsive/useFilterActions';
import { ModalDefaultDraggable } from '../../../molecules';
import PermitInspectionsForm from '../../../forms/permitInspectionForm/PermitInspectionsForm';
import { useUpdateInspectionMutation } from '../../../redux/rtkQuery/apiSlice';
import { objectKeysToSnakeCase } from '../../../utils';
import Loader from '../../../atoms/Loader';

type Props = {
  isLoading?: boolean;
};

const BulkScheduleTable = ({ isLoading = false }: Props) => {
  const { bulkActiveTab, bulkInspections } = useSelector((s: RootState) => s.bulkSlice);
  const { info } = useSelector((s: RootState) => s.userAuth);
  const [insp, setInsp] = useState<any>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [updateInspection] = useUpdateInspectionMutation();

  const headers = [
    { title: '', key: 'id', checkbox: true, selectAll: true },
    { title: 'Branch Name', key: 'franchiseName', showFilter: true },
    { title: 'Project Name', key: 'projectName', showFilter: true },
    { title: 'Permit Name', key: 'permitName', showFilter: true },
    { title: 'Address', key: 'projectAddress', showFilter: true },
    { title: 'Jurisdiction', key: 'jurisdictionName', showFilter: true },
    { title: 'Permit Number', key: 'permitNumber', showFilter: true },
    { title: 'Inspection Type', key: 'typeName', showFilter: true },
    { title: 'Inspector', key: 'inspector', showFilter: true },
    { title: 'Inspection Status', key: 'statusName', showFilter: true },
  ];

  const filteredRows = useFilterActions(bulkInspections);

  const handleCloseInspectionModal = () => {
    setIsModalOpen(false);
  };

  const onEdit = arg => {
    setIsModalOpen(true);
    setInsp(arg);
  };

  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  const handleSort = (key: string) => {
    if (sortConfig?.key === key) {
      setSortConfig({
        key,
        direction: sortConfig.direction === 'asc' ? 'desc' : 'asc',
      });
    } else {
      setSortConfig({ key, direction: 'asc' });
    }
  };

  const sortedRows = React.useMemo(() => {
    if (!sortConfig) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      const aVal = a[sortConfig.key] ?? '';
      const bVal = b[sortConfig.key] ?? '';
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortConfig.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [filteredRows, sortConfig]);

  const getSortIcon = (key: string) => {
    const active = sortConfig?.key === key;
    const direction = sortConfig?.direction;
    return <SortIcon active={active} direction={direction} onClick={() => handleSort(key)} />;
  };

  const onSubmit = async arg => {
    await updateInspection(objectKeysToSnakeCase(arg));
    setIsModalOpen(false);
  };
  return (
    <Wrapper>
      {bulkActiveTab === 'I' && (
        <TableArea>
          <TableInner>
            <TableLightBackground
              headers={headers.map(h => ({
              ...h,
              title: (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    cursor: !h.checkbox ? 'pointer' : 'default',
                    userSelect: 'none',
                  }}
                  onClick={() => !h.checkbox && handleSort(h.key)}
                >
                  <span>{h.title}</span>
                  {!h.checkbox && getSortIcon(h.key)}
                </div>
              ),
            }))}
              rows={isLoading ? [] : sortedRows}
              onEdit={info?.canScheduleInspections === false ? undefined : onEdit}
              rowsTemplate="40px repeat(9,1fr)"
            />
          </TableInner>
          {isLoading && (
            <LoaderOverlay>
              <Loader size={36} />
              <LoaderText>Loading…</LoaderText>
            </LoaderOverlay>
          )}
        </TableArea>
      )}
      {isModalOpen && (
        <ModalDefaultDraggable title="Edit Inspection" onClose={handleCloseInspectionModal}>
          <PermitInspectionsForm
            projectId={insp.projectId}
            onSubmit={onSubmit}
            initialValues={insp}
            modalError={false}
            addButtonText="Create"
            updateButtonText="Update"
            initialErrors={{}}
            permit={insp.permitId}
            permitStatus="permitStatus"
            isReadOnly={false}
            setInitialValues={setInsp}
            updateTable={() => {}}
          />
        </ModalDefaultDraggable>
      )}
    </Wrapper>
  );
};

const payload = {
  inspectorId: 8,
  inspections: [
    { inspectionId: 1234, date: '08/25/2025' },
    { inspectionId: 1235, date: '08/25/2025' },
    { inspectionId: 1236, date: '08/25/2025' },
  ],
};

export default BulkScheduleTable;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  margin-left: 32px;
  margin-right: 32px;
  padding-bottom: 20px;
`;

/* TableLightBackground returns <ConfirmationDialog /> + <TableCard />.
   The TableCard is the last direct child; stretch it to fill available height
   and let its own contents scroll internally. */
const TableArea = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const TableInner = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  > div:last-child {
    flex: 1;
    min-height: 0;
    overflow: auto;
  }
`;

const LoaderOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 10px;
  z-index: 2;
  pointer-events: none;
`;

const LoaderText = styled.div`
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif;
  font-size: 0.875rem;
  color: var(--neutral-500);
`;

const SortIcon = styled.span<{ active?: boolean; direction?: 'asc' | 'desc' }>`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 12px;
  margin-left: 6px;
  cursor: pointer;
  user-select: none;
  opacity: ${({ active }) => (active ? 1 : 0.85)};

  &::before,
  &::after {
    font-size: 11px;
    line-height: 10px; /* adds space between ▲ and ▼ */
    color: #4b5563; /* default medium-dark gray */
    font-weight: 600;
  }

  &::before {
    content: '▲';
    color: ${({ direction }) => (direction === 'asc' ? '#1f2937' : '#6b7280')};
    margin-bottom: 1px; /* 👈 creates visible space */
  }

  &::after {
    content: '▼';
    color: ${({ direction }) => (direction === 'desc' ? '#1f2937' : '#6b7280')};
    margin-top: 1px; /* 👈 extra spacing below */
  }

  &:hover {
    opacity: 1;
  }
`;
